import { format, parseISO } from 'date-fns'
import { enUS, es } from 'date-fns/locale'

export const formatDate = (date: string) => {
  const today = new Date()
  const todayYear = today.getFullYear()
  if (Date.parse(date)) {
    const givenDate = new Date(date)
    if (givenDate.getFullYear() > todayYear) givenDate.setFullYear(givenDate.getFullYear() - 100)
    return format(givenDate, 'dd/MM/yyyy', { locale: es })
  }
}

export const toISOString = (date: Date | string) => {
  date = typeof date === 'string' ? new Date(date) : date
  const pad = (num: number) => (num < 10 ? '0' : '') + num

  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    '.000' +
    'Z'
  )
}

export const restoreLSData = (key: string) => {
  const storageData = localStorage.getItem(key)
  return storageData ? JSON.parse(storageData) : null
}

export const formatDetailDate = (date: string) => {
  if (Date.parse(date)) return format(parseISO(date), 'dd/MM/yyyy HH:mm:ss', { locale: es })
}

export const formatBirthDateInverse = (dateString: any) => {
  const today = new Date()
  const date: string = dateString.split('/')
  const parsed = date[1] + '/' + date[0] + '/' + date[2]
  const dateParsed = new Date(parsed)
  if (dateParsed.getFullYear() > today.getFullYear()) dateParsed.setFullYear(dateParsed.getFullYear() - 100)
  return format(dateParsed, "yyyy-MM-dd'T'HH:mm:ss", { locale: enUS })
}

const dist2 = (v: any, w: any) => Math.pow(v.x - w.x, 2) + Math.pow(v.y - w.y, 2)

interface Coord {
  x: number
  y: number
}

function distToSegmentSquared(p: Coord, v: Coord, w: Coord) {
  // p es point, v es firstPoint, w es lastPoint
  const line = dist2(v, w)
  if (line === 0) return dist2(p, v)
  let long = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / line
  long = Math.max(0, Math.min(1, long))
  return dist2(p, {
    x: v.x + long * (w.x - v.x),
    y: v.y + long * (w.y - v.y)
  })
}
export const distToSegment = (p: any, v: any, w: any) => Math.sqrt(distToSegmentSquared(p, v, w))

// Esto se puede eliminar cuando se trabaje sobre ARGENTINA y se utilice OBD y manejo de imagenes por otro lado
export const factorizePDF417 = (data: string) => {
  let dniData = {}
  if (data.startsWith('@')) {
    const oldDNI = data.slice(1).split('@')
    dniData = {
      documentNumber: oldDNI[0].trim(),
      names: oldDNI[4],
      lastName: oldDNI[3],
      nationality: oldDNI[5],
      birthDate: formatBirthDateInverse(oldDNI[6]),
      sex: oldDNI[7]
    }
  } else {
    const newDNI = data.slice(1).split('@')
    dniData = {
      documentNumber: newDNI[4],
      names: newDNI[2],
      lastName: newDNI[1],
      nationality: 'ARGENTINA',
      birthDate: formatBirthDateInverse(newDNI[6]),
      sex: newDNI[3]
    }
  }
  return dniData
}

export const megaByteSize = (str: any) => new Blob([str]).size / 1024.0 / 1024.0

// Esto se puede eliminar cuando se trabaje sobre ARGENTINA y se utilice OBD y manejo de imagenes por otro lado
export const resizeImage = async (dataUrl: string, maxWidth: number = 1200, maxHeight: number = 1200) => {
  return await new Promise((resolve) => {
    const img = new Image()
    img.src = dataUrl
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const MAX_WIDTH = maxWidth
      const MAX_HEIGHT = maxHeight
      let width = img.width
      let height = img.height

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width
          width = MAX_WIDTH
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height
          height = MAX_HEIGHT
        }
      }
      canvas.width = width
      canvas.height = height
      const ctx: any = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL())
    }
  })
}

// Esto se puede eliminar cuando se trabaje sobre ARGENTINA y se utilice OBD y manejo de imagenes por otro lado
export const imageWeightControl = async (
  frontPicture: string = '',
  backPicture: string = '',
  extraPicture: string = ''
) => {
  // Achico las imagenes
  // Con un limite de ancho/alto de 800, se obtienen imagen de aprox 1mb
  // La request maxima son 1mb. De todas formas depende de la compresion de la imagen
  let maxPixels = 800
  let suitableSize = false
  let front: any = frontPicture
  let back: any = backPicture
  let extra: any = extraPicture
  if (!backPicture.length) suitableSize = megaByteSize(front) < 1
  else if (!extraPicture.length) suitableSize = megaByteSize(front) + megaByteSize(back) < 3
  else suitableSize = megaByteSize(front) + megaByteSize(back) + megaByteSize(extra) < 5
  // Debido a que no se puede saber de antemano si la compresion de la imagen
  // Va a resultar en menos de 1 mb (esto depende mucho el tipo de foto que se saque, si tiene fondos complejos o no)
  // Se hace una reduccion progresiva en caso de que el tamaño supere los 1 mb
  while (!suitableSize) {
    // Se verifica que sea menor a 1mb cada imagen
    if (!backPicture) {
      front = await resizeImage(frontPicture, maxPixels, maxPixels)
      suitableSize = megaByteSize(front) < 1
    } else if (!extraPicture) {
      front = await resizeImage(frontPicture, maxPixels, maxPixels)
      back = await resizeImage(backPicture, maxPixels, maxPixels)
      suitableSize = megaByteSize(front) + megaByteSize(back) < 3
    } else {
      front = await resizeImage(frontPicture, maxPixels, maxPixels)
      back = await resizeImage(backPicture, maxPixels, maxPixels)
      extra = await resizeImage(extraPicture, maxPixels, maxPixels)
      suitableSize = megaByteSize(front) + megaByteSize(back) + megaByteSize(extra) < 5
    }
    maxPixels *= 0.9
  }
  front = front.split('base64,')[1]
  back = back.length && back.split('base64,')[1]
  extra = extra.length && extra.split('base64,')[1]
  return [front, back, extra]
}

export const parameterizeDate = (parameter: number) => {
  // Parametrizamos una fecha con un valor dinámico a partir de la fecha actual y la devuelve en formato ISO String
  const date = new Date()
  date.setDate(date.getDate() + parameter)
  return date.toISOString()
}

export const sortReferrsByDate = (a: Record<string, string>, b: Record<string, string>) => {
  if (a.ppp_order < b.ppp_order) return 1
  if (a.ppp_order > b.ppp_order) return -1
  return 0
}

export const verifyIfCanStringify = (input: any) => {
  if (typeof input === 'string') return false
  try {
    const body = JSON.stringify(input)
    if (body) return true
  } catch (error) {
    return false
  }
}

export const replaceWhiteSpaces = (value: string) => value.replace(' ', '')
